import React, { useMemo } from 'react';
import VideoFilterBar from './VideoFilterBar';
import VideoGenerateForm from './VideoGenerateForm';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { Video } from 'lucide-react';
import SidebarHeader from '@/components/ui/SidebarHeader';
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface VideoFilterSectionProps {
  hasAudio: boolean;
  onAudioChange: (hasAudio: boolean) => void;
  styleOptions: FilterOption[];
  selectedStyle: string | null;
  onStyleChange: (options: FilterOption[]) => void;
  setSelectedStyle: (style: string | null) => void;
  influenceOptions: FilterOption[];
  selectedInfluence: string | null;
  onInfluenceChange: (options: FilterOption[]) => void;
  setSelectedInfluence: (influence: string | null) => void;
  colorOptions: FilterOption[];
  onColorChange: (options: FilterOption[]) => void;
  backgroundOptions: FilterOption[];
  onBackgroundChange: (options: FilterOption[]) => void;
  selectedBackground: string | null;
  environmentOptions: FilterOption[];
  onEnvironmentChange: (options: FilterOption[]) => void;
  moodOptions: FilterOption[];
  onMoodChange: (options: FilterOption[]) => void;
  videoDescription: string;
  onVideoDescriptionChange: (value: string) => void;
  isGenerating: boolean;
  onGenerate: () => void;
}

const VideoFilterSection: React.FC<VideoFilterSectionProps> = React.memo(({
  hasAudio,
  onAudioChange,
  styleOptions,
  selectedStyle,
  onStyleChange,
  setSelectedStyle,
  influenceOptions,
  selectedInfluence,
  onInfluenceChange,
  setSelectedInfluence,
  colorOptions,
  onColorChange,
  backgroundOptions,
  onBackgroundChange,
  selectedBackground,
  environmentOptions,
  onEnvironmentChange,
  moodOptions,
  onMoodChange,
  videoDescription,
  onVideoDescriptionChange,
  isGenerating,
  onGenerate
}) => {
  const isMobile = useIsMobile();
  
  // Memoized props for VideoFilterBar to prevent unnecessary re-renders
  const filterBarProps = useMemo(() => ({
    hasAudio,
    onAudioChange,
    styleOptions,
    selectedStyle,
    onStyleChange,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    onInfluenceChange,
    setSelectedInfluence,
    colorOptions,
    onColorChange,
    backgroundOptions,
    onBackgroundChange,
    selectedBackground,
    environmentOptions,
    onEnvironmentChange,
    moodOptions,
    onMoodChange,
    videoDescription,
    onVideoDescriptionChange
  }), [
    hasAudio,
    onAudioChange,
    styleOptions,
    selectedStyle,
    onStyleChange,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    onInfluenceChange,
    setSelectedInfluence,
    colorOptions,
    onColorChange,
    backgroundOptions,
    onBackgroundChange,
    selectedBackground,
    environmentOptions,
    onEnvironmentChange,
    moodOptions,
    onMoodChange,
    videoDescription,
    onVideoDescriptionChange
  ]);

  // Memoized props for VideoGenerateForm to prevent unnecessary re-renders
  const generateFormProps = useMemo(() => ({
    videoDescription,
    isGenerating,
    hasAudio,
    styleOptions,
    selectedStyle,
    influenceOptions,
    selectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    environmentOptions,
    moodOptions,
    onGenerate
  }), [
    videoDescription,
    isGenerating,
    hasAudio,
    styleOptions,
    selectedStyle,
    influenceOptions,
    selectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    environmentOptions,
    moodOptions,
    onGenerate
  ]);
  
  return (
    <MobileSidebarAccordion 
      variant="purple" 
      persistKey="video-generator-sidebar"
      defaultExpanded={false}
      className="h-full"
    >
      <SidebarHeader title="Create Video" icon={Video} variant="purple" />
      
      {/* Each component must be a direct child of MobileSidebarAccordion */}
      <VideoFilterBar {...filterBarProps} />
      <VideoGenerateForm {...generateFormProps} />
    </MobileSidebarAccordion>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.hasAudio === nextProps.hasAudio &&
    prevProps.selectedStyle === nextProps.selectedStyle &&
    prevProps.selectedInfluence === nextProps.selectedInfluence &&
    prevProps.selectedBackground === nextProps.selectedBackground &&
    prevProps.videoDescription === nextProps.videoDescription &&
    prevProps.isGenerating === nextProps.isGenerating &&
    prevProps.onAudioChange === nextProps.onAudioChange &&
    prevProps.onStyleChange === nextProps.onStyleChange &&
    prevProps.onInfluenceChange === nextProps.onInfluenceChange &&
    prevProps.onBackgroundChange === nextProps.onBackgroundChange &&
    prevProps.onVideoDescriptionChange === nextProps.onVideoDescriptionChange &&
    prevProps.onGenerate === nextProps.onGenerate
  );
});

VideoFilterSection.displayName = 'VideoFilterSection';

export default VideoFilterSection; 